import { useQuery, UseQueryOptions } from '@tanstack/react-query';
import {
  getMancoCisPage,
  getMancoCounterpartyDropdown,
  getMancoCounterpartyExposurePage,
  getMancoEquityPage,
  getMancoExposurePage,
  getMancoFixedIncomePage,
} from '../manco.routes';
import type {
  MancoCisPageModel,
  MancoCounterpartyDropdownPayload,
  MancoCounterpartyExposurePageModel,
  MancoEquityPageModel,
  MancoExposurePageModel,
  MancoFixedIncomePageModel,
} from '../types';

export const mancoExposureQueryKey = (
  positionDate?: string,
  raptorPlatformId?: string,
) => ['manco-exposure', positionDate, raptorPlatformId] as const;

export const useMancoExposurePageModel = (
  positionDate?: string,
  raptorPlatformId?: string,
  options?: Omit<
    UseQueryOptions<MancoExposurePageModel, Error>,
    'queryKey' | 'queryFn' | 'enabled'
  >,
) => {
  return useQuery<MancoExposurePageModel, Error>({
    queryKey: mancoExposureQueryKey(positionDate, raptorPlatformId),
    queryFn: () => getMancoExposurePage(positionDate!, raptorPlatformId),
    enabled: !!positionDate && (options?.enabled ?? true),
    staleTime: 30 * 60 * 1000,
    ...options,
  });
};

export const mancoEquityQueryKey = (positionDate?: string) =>
  ['manco-equity', positionDate] as const;

export const useMancoEquityPageModel = (
  positionDate?: string,
  options?: Omit<
    UseQueryOptions<MancoEquityPageModel, Error>,
    'queryKey' | 'queryFn'
  >,
) => {
  return useQuery<MancoEquityPageModel, Error>({
    queryKey: mancoEquityQueryKey(positionDate),
    queryFn: () => getMancoEquityPage(positionDate),
    staleTime: 5 * 60 * 1000,
    ...options,
  });
};

export const mancoFixedIncomeQueryKey = (positionDate?: string) =>
  ['manco-fixed-income', positionDate] as const;

export const useMancoFixedIncomePageModel = (
  positionDate?: string,
  options?: Omit<
    UseQueryOptions<MancoFixedIncomePageModel, Error>,
    'queryKey' | 'queryFn'
  >,
) => {
  return useQuery<MancoFixedIncomePageModel, Error>({
    queryKey: mancoFixedIncomeQueryKey(positionDate),
    queryFn: () => getMancoFixedIncomePage(positionDate),
    staleTime: 5 * 60 * 1000,
    ...options,
  });
};

export const mancoCisQueryKey = (positionDate?: string) =>
  ['manco-cis', positionDate] as const;

export const useMancoCisPageModel = (
  positionDate?: string,
  options?: Omit<
    UseQueryOptions<MancoCisPageModel, Error>,
    'queryKey' | 'queryFn'
  >,
) => {
  return useQuery<MancoCisPageModel, Error>({
    queryKey: mancoCisQueryKey(positionDate),
    queryFn: () => getMancoCisPage(positionDate),
    staleTime: 5 * 60 * 1000,
    ...options,
  });
};

export const useMancoCounterpartyDropdownQuery = (
  options?: Omit<
    UseQueryOptions<MancoCounterpartyDropdownPayload, Error>,
    'queryKey' | 'queryFn'
  >,
) => {
  return useQuery<MancoCounterpartyDropdownPayload, Error>({
    queryKey: ['manco-counterparty-dropdown'],
    queryFn: () => getMancoCounterpartyDropdown(),
    staleTime: 30 * 60 * 1000,
    ...options,
  });
};

export const mancoCounterpartyExposureQueryKey = (
  counterparty?: string | null,
  positionDate?: string,
) => ['manco-counterparty-exposure', counterparty, positionDate] as const;

export const useMancoCounterpartyExposurePageModel = (
  counterparty: string | null,
  positionDate?: string,
  options?: Omit<
    UseQueryOptions<MancoCounterpartyExposurePageModel, Error>,
    'queryKey' | 'queryFn' | 'enabled'
  >,
) => {
  return useQuery<MancoCounterpartyExposurePageModel, Error>({
    queryKey: mancoCounterpartyExposureQueryKey(counterparty, positionDate),
    queryFn: () =>
      getMancoCounterpartyExposurePage(counterparty!, positionDate),
    staleTime: 30 * 60 * 1000,
    enabled:
      !!counterparty &&
      counterparty !== 'None Selected' &&
      counterparty !== 'none_selected' &&
      (options?.enabled ?? true),
    ...options,
  });
};
